import React, {CSSProperties, ReactNode, RefObject, useCallback, useLayoutEffect, useRef, useState} from 'react'

/**
 * Floats children as a `position: fixed` overlay covering `scrollContainerRef`'s viewport box.
 * Pre-latch the overlay's top edge is offset by `scrollOffset - scrollTop` via a `translateY`
 * transform, so it appears anchored to content at `scrollOffset`. Once the user scrolls past
 * the threshold the offset clamps to 0 and the overlay latches, regardless of further scrolling.
 *
 * The transform is the only thing that moves per scroll event — a single composite-only write per
 * event, no layout, no paint, no React re-render.
 *
 * Renders nothing until initial setup completes, so the user never sees a transient frame in the
 * wrong position.
 *
 * Never unlatches within a single component instance lifecycle, even if `scrollOffset` changes.
 */
type LatchingOverlayProps = {
  scrollContainerRef: RefObject<HTMLElement | null>
  children: ReactNode
  style?: Omit<CSSProperties, 'transform'>
  scrollOffset: number
}

type Rect = {top: number; left: number; width: number; height: number}
type State = {kind: 'unset'} | ({kind: 'tracking'} & Rect) | ({kind: 'latched'} & Rect)

function readRect(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect()
  return {top: r.top, left: r.left, width: r.width, height: r.height}
}

export function LatchingOverlay({scrollContainerRef, children, style, scrollOffset}: LatchingOverlayProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const [state, setState] = useState<State>({kind: 'unset'})

  // Only allow effective offset to decrease rather than decreasing, allowing the overlay to
  // adjust to cover the viewport if scroll height shrinks, but preventing it from jumping
  // when scroll height grows due to hitting the boundary of an infinitely scrolling container
  const minEffectiveRef = useRef(scrollOffset)
  const effectiveOffset = useCallback(
    (container: HTMLElement): number => {
      const clamped = Math.min(scrollOffset, Math.max(0, container.scrollHeight - container.clientHeight))
      if (clamped < minEffectiveRef.current) minEffectiveRef.current = clamped
      return minEffectiveRef.current
    },
    [scrollOffset]
  )

  // Initial setup. Runs only when unset — once latched, prop changes can't drop us back.
  useLayoutEffect(() => {
    if (state.kind !== 'unset') return
    const container = scrollContainerRef.current
    if (!container) return

    const rect = readRect(container)
    const translate = Math.max(0, effectiveOffset(container) - container.scrollTop)
    setState({kind: translate <= 0 ? 'latched' : 'tracking', ...rect})
  }, [effectiveOffset, scrollContainerRef, scrollOffset, state.kind])

  // While tracking: keep the transform in sync with scroll position, and latch when the
  // offset reaches zero. The layout-effect write covers the initial tracking render and
  // any `scrollOffset` change; the scroll listener covers per-scroll updates.
  useLayoutEffect(() => {
    if (state.kind !== 'tracking') return
    const container = scrollContainerRef.current
    const overlay = overlayRef.current
    if (!container || !overlay) return

    const updateTransform = () => {
      const translate = Math.max(0, effectiveOffset(container) - container.scrollTop)
      overlay.style.transform = `translateY(${translate}px)`
      if (translate <= 0) setState({kind: 'latched', ...readRect(container)})
    }

    updateTransform()
    container.addEventListener('scroll', updateTransform, {passive: true})
    return () => container.removeEventListener('scroll', updateTransform)
  }, [state.kind, scrollContainerRef, scrollOffset, effectiveOffset])

  // Keep the fixed coords aligned with the container while mounted.
  useLayoutEffect(() => {
    if (state.kind === 'unset') return
    const container = scrollContainerRef.current
    const overlay = overlayRef.current
    if (!container || !overlay) return

    const reposition = () => {
      const rect = readRect(container)
      overlay.style.top = `${rect.top}px`
      overlay.style.left = `${rect.left}px`
      overlay.style.width = `${rect.width}px`
      overlay.style.height = `${rect.height}px`
    }

    // On resize the container's scrollable range may shrink below the current effective
    // offset; recompute so we latch if the clamped offset has reached zero. Window scroll
    // doesn't affect scrollHeight/clientHeight, so it doesn't need this.
    const checkLatch = () => {
      if (state.kind !== 'tracking') return
      const translate = Math.max(0, effectiveOffset(container) - container.scrollTop)
      overlay.style.transform = `translateY(${translate}px)`
      if (translate <= 0) setState({kind: 'latched', ...readRect(container)})
    }

    const onResize = () => {
      reposition()
      checkLatch()
    }

    const resizeObserver = new ResizeObserver(onResize)
    resizeObserver.observe(container)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', reposition, {passive: true})

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', reposition)
    }
  }, [state.kind, scrollContainerRef, scrollOffset, effectiveOffset])

  if (state.kind === 'unset') return null

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        top: state.top,
        left: state.left,
        width: state.width,
        height: state.height,
        willChange: 'transform',
        // Transform is written imperatively by the scroll handler so each scroll event
        // paints in the next frame without a React render cycle
        ...style
      }}
    >
      {children}
    </div>
  )
}
